/**
 * Club signup, wherever it appears.
 *
 * Wires any [data-club-form] on the page: validates the email and the Israeli
 * mobile number, insists on explicit consent, and posts the subscriber to the
 * Klaviyo EMAIL list.
 *
 * Email, not SMS, because the account has no registered SMS sender - Klaviyo
 * cannot text anyone, not even the opt-in confirmation. The phone is still
 * collected and stored on the profile, so the SMS audience is being built in
 * the meantime and needs no second ask once a sender exists.
 *
 * The list id is REQUIRED here. /client/subscriptions/ is the public,
 * key-less endpoint behind custom signup forms, and it takes no consent
 * object of its own: the LIST decides the channel and the opt-in process.
 * That is also why a `subscriptions` block on the profile is rejected
 * outright - that field belongs to the server-side Subscribe Profiles
 * endpoint, which needs a private key and cannot run in a storefront.
 */

/** Good enough: the server is the real validator, this only catches typos. */
const isEmail = (raw) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(raw).trim());

/** Israeli mobile numbers, typed the way people actually type them. */
function toE164(raw) {
  const digits = String(raw).replace(/[^\d+]/g, '');
  if (/^\+972\d{8,9}$/.test(digits)) return digits;
  if (/^972\d{8,9}$/.test(digits)) return `+${digits}`;
  if (/^0\d{8,9}$/.test(digits)) return `+972${digits.slice(1)}`;
  return null;
}

function wire(form) {
  const status = form.querySelector('[data-club-status]');
  const company = form.dataset.klaviyoCompany;
  const list = form.dataset.klaviyoList;

  const say = (message, kind) => {
    if (!status) return;
    status.textContent = message;
    status.dataset.kind = kind;
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const nameInput = form.querySelector('input[name="name"]');
    const emailInput = form.querySelector('input[name="email"]');
    const phoneInput = form.querySelector('input[name="phone"]');
    const consent = form.querySelector('input[name="consent"]');
    const button = form.querySelector('button[type="submit"]');

    const fullName = (nameInput?.value ?? '').trim();
    if (nameInput && fullName.length < 2) {
      say('נא להזין שם מלא', 'error');
      nameInput.focus();
      return;
    }

    const email = (emailInput?.value ?? '').trim();
    if (!isEmail(email)) {
      say('נא להזין כתובת מייל תקינה', 'error');
      emailInput?.focus();
      return;
    }

    const phone = toE164(phoneInput?.value ?? '');
    if (!phone) {
      say('נא להזין מספר טלפון נייד תקין', 'error');
      phoneInput?.focus();
      return;
    }
    if (!consent?.checked) {
      say('צריך לאשר קבלת הודעות כדי להצטרף', 'error');
      return;
    }
    if (!company || !list) {
      say('ההרשמה עדיין לא מחוברת. חסרה הגדרת קלביו.', 'error');
      return;
    }

    button?.setAttribute('disabled', '');
    say('רק רגע...', 'pending');

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Footer club form',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      phone_number: phone,
                      first_name: fullName.split(/\s+/)[0] || undefined,
                      last_name: fullName.split(/\s+/).slice(1).join(' ') || undefined,
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: list } } },
            },
          }),
        }
      );
      if (!response.ok) {
        /* The shopper gets the friendly line below; the reason goes to the
           console, because "it does not work" is not something a Klaviyo
           account id or a revoked key tells you from the outside. */
        console.error('[club signup] Klaviyo refused the subscription:', response.status, await response.text());
        throw new Error(String(response.status));
      }
      say('הצטרפת למועדון. קוד ההנחה בדרך אליך במייל.', 'success');
      form.querySelectorAll('.club-form__input--stacked').forEach((el) => el.setAttribute('hidden', ''));
      form.querySelector('.club-form__row')?.setAttribute('hidden', '');
      form.querySelector('.club-form__consent')?.setAttribute('hidden', '');
    } catch {
      say('משהו השתבש. אפשר לנסות שוב בעוד רגע.', 'error');
      button?.removeAttribute('disabled');
    }
  });
}

document.querySelectorAll('[data-club-form]').forEach(wire);
